import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useParams, useNavigate} from 'react-router-dom';
import './RequirementsPage.css';
import axiosInstance from '../api';
import ProgressBar from '@ramonak/react-progress-bar';

function RequirementsPage() {
  const { t } = useTranslation();
  // Helper functions for status indicators
  const getStatusColor = (status) => {
    switch((status || '').toUpperCase()) {
      case 'IMPLEMENTED': return '#22c55e';
      case 'IN_PROGRESS': return '#f59e0b';
      case 'NOT_IMPLEMENTED': return '#ef4444';
      default: return '#64748b';
    }
  };

  const getStatusIcon = (status) => {
    switch((status || '').toUpperCase()) {
      case 'IMPLEMENTED': return 'fas fa-check-circle';
      case 'IN_PROGRESS': return 'fas fa-clock';
      case 'NOT_IMPLEMENTED': return 'fas fa-times-circle';
      default: return 'fas fa-question-circle';
    }
  };

  const { frameworkId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [expandedSubCategory, setExpandedSubCategory] = useState({});
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [controlOwners, setControlOwners] = useState([]);
  const [auditProgressStats, setAuditProgressStats] = useState({});
  const [documentName, setDocumentName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const tabRef = useRef(null);

  useEffect(() => {
    
    const fetchAuditProgressStats = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
  
        const response = await axiosInstance.get(
          `audit/${frameworkId}/audit-progress`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        setAuditProgressStats(response.data);
      } catch (err) {
        setError(err.message);
      }
    };
  
    fetchAuditProgressStats();
  }, [frameworkId]);
  

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }

        const response = await axiosInstance.get(
          `framework/${frameworkId}/requirement-categories`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCategories(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axiosInstance.get("/users", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const updatedUsers = response.data.map((user) => ({
          ...user,
          organizationName: user.organization
            ? user.organization.organizationName
            : "N/A",
        }));
        setUsers(updatedUsers);
      } catch { /* non-critical */ }
    };

    const role = localStorage.getItem('role');
    setUserRole(role);

    fetchCategories();
    fetchUsers();
  }, [frameworkId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tabRef.current && !tabRef.current.contains(event.target)) {
        setSelectedSubCategory(null);
        setSelectedControl(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCategoryClick = async (categoryId) => {

    setSelectedCategoryId(categoryId);
    setLoadingSubCategories(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing.');
      }
  
      const subCategoryResponse = await axiosInstance.get(`requirement-category/${categoryId}/subcategories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      setSubCategories(subCategoryResponse.data.subCategories || []);  
      const ownersResponse = await axiosInstance.get(`audit/${categoryId}/controls/owners`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
  
      setControlOwners(ownersResponse.data);
  
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const handleControlClick = async (control) => {
    setSelectedControl(control);
    setSelectedSubCategory(null);
  
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing.');
      }
  
      const response = await axiosInstance.get(`/document-controls/${control.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      const documents = response.data.map((doc) => ({
        id: doc.id,
        documentId: doc.documentId,
        title: doc.title,
        description: doc.description,
      }));
  
      setDocumentName(documents);
    } catch {
      setDocumentName([]);
    }
  };
  

  const handleDocumentClick = (documentId) => {
    const document = documentName.find((doc) => doc.id === documentId);
    if (document) {
      navigate('/dashboard/document-details', { state: { document } });
    }
  };

  const handleOwnerChange = async (e) => {
    const selectedUserId = e.target.value;
  
    if (!selectedUserId) {
      setSelectedControl((prevControl) => ({
        ...prevControl,
        owner: '',
      }));
      return;
    }
  
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing.');
      }
  
      const assignOwnerResponse = await axiosInstance.post(
        `audit/control/${selectedControl.id}/${selectedUserId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
  
      const { userName, firstName, lastName } = assignOwnerResponse.data;
  
      setSelectedControl((prevControl) => ({
        ...prevControl,
        owner: `${firstName} ${lastName}`,
      }));
  
      setControlOwners((prevOwners) => [
        ...prevOwners,
        { userName, firstName, lastName, controlId: selectedControl.id }
      ]);
  
      if (selectedSubCategory) {
        const updatedControls = selectedSubCategory.controls.map((control) =>
          control.id === selectedControl.id
            ? { ...control, owner: `${firstName} ${lastName}` }
            : control
        );
        setSubCategories((prevSubCategories) =>
          prevSubCategories.map((subCategory) =>
            subCategory.id === selectedSubCategory.id
              ? { ...subCategory, controls: updatedControls }
              : subCategory
          )
        );
      }

      handleCloseTab();
  
      const ownersResponse = await axiosInstance.get(`audit/${selectedCategoryId}/controls/owners`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
  
      setControlOwners(ownersResponse.data);
  
    } catch { /* owner assignment failed */ }
  };

  const handleCloseTab = () => {
    setSelectedSubCategory(null);
    setSelectedControl(null);
  };

  const limitWords = (text, wordLimit) => {
    if (!text) return '';

    const words = text.split(' ');

    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...';
    }

    return text;
  };


  const handleSubCategoryClick = (subCategory) => {
    setSelectedSubCategory(subCategory);
  };

  const handleToggleSubCategory = (subCategoryId) => {
    setExpandedSubCategory((prevExpanded) => ({
      ...prevExpanded,
      [subCategoryId]: !prevExpanded[subCategoryId],
    }));
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem' }}><div style={{ width: 28, height: 28, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.75s linear infinite' }} /></div>;
  if (error) return <div style={{ padding: '1rem', color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: '0.875rem' }}>{t("RequirementsPage.errorPrefix", { error })}</div>;

  return (
    <div className="requirements-container">
      {/* Modern Dashboard Header */}
      <div className="requirements-header">
        <div className="header-top-row">
          <div className="header-left-group">
            <div className="header-icon-badge">
              <i className="fas fa-clipboard-list"></i>
            </div>
            <div className="header-title">
              <h2>{t("RequirementsPage.complianceRequirements")}</h2>
              <span className="framework-name">
                {categories.length > 0 ? categories[0].frameworkName || t("RequirementsPage.frameworkCategories") : t("RequirementsPage.frameworkCategories")}
              </span>
              <button className="back-button compact" onClick={() => navigate('/dashboard/compliance/frameworks')} title={t("RequirementsPage.backToFrameworks")}>
                <i className="fas fa-arrow-left"></i>
                <span>{t("RequirementsPage.frameworks")}</span>
              </button>
            </div>
          </div>
          <div className="header-metrics">
            <div className="metric-badge">
              <div className="metric-value">{categories.length}</div>
              <div className="metric-label">{t("RequirementsPage.categoriesLabel")}</div>
            </div>
            <div className="metric-badge highlight">
              <div className="metric-value">{auditProgressStats.controls || '0'}%</div>
              <div className="metric-label">{t("RequirementsPage.completionLabel")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Two-column Layout with Sidebar + Main Panel */}
      <div className="requirements-layout">
        {/* Simple, Direct Sidebar Navigation */}
        <div className="requirements-sidebar">
          <div className="sidebar-header">
            <h3>{t("RequirementsPage.requirementCategories")}</h3>
            <span className="categories-count">{categories.length}</span>
          </div>
          
          <div className="simple-categories-list">
            {/* Direct rendering of categories without conditionals */}
            {categories.map((category, idx) => {
              // Fixed set of colors
              const colors = ['#f59e0b', '#22c55e', '#ef4444', '#a855f7', '#3b82f6'];
              const icons = ['fa-shield-alt', 'fa-user-shield', 'fa-file-contract', 'fa-globe', 'fa-cogs'];
              
              const accent = colors[idx % colors.length];
              const icon = icons[idx % icons.length];
              
              return (
                <div
                  key={category.id}
                  className={`sidebar-category-item ${selectedCategoryId === category.id ? 'active-category' : ''}`}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <div className="sidebar-category-left">
                    <div className="sidebar-category-icon" style={{ backgroundColor: accent + '18', color: accent }}>
                      <i className={`fas ${icon}`}></i>
                    </div>
                  </div>
                  
                  <div className="sidebar-category-right">
                    <div className="sidebar-category-name">{category.name}</div>
                    <div className="sidebar-category-progress">
                      <div className="sidebar-progress-bar">
                        <div 
                          className="sidebar-progress-fill" 
                          style={{ width: `${category.completion || 0}%`, backgroundColor: accent }}
                        ></div>
                      </div>
                      <span className="sidebar-progress-text">{category.completion || 0}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Main Content Panel */}
        <div className="requirements-main">
          {selectedCategoryId ? (
            <div className="selected-category-panel">
              {categories.map((category, idx) => {
                if (category.id === selectedCategoryId) {
                  const accentColors = [
                    '#f59e0b', // Orange (Vendors)
                    '#22c55e', // Green (Assets)
                    '#ef4444', // Red (Vulnerabilities)
                    '#a855f7', // Purple (Tests)
                    '#3b82f6', // Blue (Compliance)
                  ];
                  const accent = accentColors[idx % accentColors.length];
                  
                  return (
                    <div key={category.id} className="category-details">
                      <div className="category-details-header">
                        <h3>{category.name}</h3>
                        <div className="category-meta">
                          <span className="category-type" style={{ background: accent + '14', color: accent }}>
                            {t("RequirementsPage.categoryNumber", { number: idx + 1 })}
                          </span>
                          <span className="category-items">{t("RequirementsPage.requirementsCount", { count: category.itemCount || 0 })}</span>
                        </div>
                        <p className="category-description">{category.description || t("RequirementsPage.noCategoryDescription")}</p>
                      </div>
                      
                      <div className="category-metrics-panel">
                        <div className="metrics-row">
                          <div className="metric-card">
                            <div className="metric-icon" style={{ background: accent + '14', color: accent }}>
                              <i className="fas fa-clipboard-check"></i>
                            </div>
                            <div className="metric-info">
                              <span className="metric-label">{t("RequirementsPage.controls")}</span>
                              <span className="metric-value">{auditProgressStats.controls || 0}%</span>
                            </div>
                            <div className="metric-progress">
                              <ProgressBar
                                completed={auditProgressStats.controls || 0}
                                bgColor={accent}
                                baseBgColor="#e5e7eb"
                                height="8px"
                                labelColor="#fff"
                                isLabelVisible={false}
                                borderRadius="4px"
                              />
                            </div>
                          </div>
                          
                          <div className="metric-card">
                            <div className="metric-icon" style={{ background: '#22c55e14', color: '#22c55e' }}>
                              <i className="fas fa-vial"></i>
                            </div>
                            <div className="metric-info">
                              <span className="metric-label">{t("RequirementsPage.tests")}</span>
                              <span className="metric-value">{auditProgressStats.executedTests || 0}%</span>
                            </div>
                            <div className="metric-progress">
                              <ProgressBar
                                completed={auditProgressStats.executedTests || 0}
                                bgColor="#22c55e"
                                baseBgColor="#e5e7eb"
                                height="8px"
                                labelColor="#fff"
                                isLabelVisible={false}
                                borderRadius="4px"
                              />
                            </div>
                          </div>
                          
                          <div className="metric-card">
                            <div className="metric-icon" style={{ background: '#a855f714', color: '#a855f7' }}>
                              <i className="fas fa-file-alt"></i>
                            </div>
                            <div className="metric-info">
                              <span className="metric-label">{t("RequirementsPage.documents")}</span>
                              <span className="metric-value">{auditProgressStats.documentProgress || 0}%</span>
                            </div>
                            <div className="metric-progress">
                              <ProgressBar
                                completed={auditProgressStats.documentProgress || 0}
                                bgColor="#a855f7"
                                baseBgColor="#e5e7eb"
                                height="8px"
                                labelColor="#fff"
                                isLabelVisible={false}
                                borderRadius="4px"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="modern-subcategory-section">
                        <div className="modern-subcategory-header">
                          <div className="modern-header-left">
                            <h3>{t("RequirementsPage.subcategories")}</h3>
                            <div className="subcategory-stats">
                              <span className="stats-badge">{t("RequirementsPage.subcategoriesTotal", { count: subCategories.length })}</span>
                            </div>
                          </div>
                          <div className="modern-header-right">
                            <div className="subcategory-actions-bar">
                              <div className="search-subcategories">
                                <i className="fas fa-search"></i>
                                <input
                                  type="text"
                                  placeholder={t("RequirementsPage.searchSubcategories")}
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                />
                              </div>
                              <div className="view-toggle">
                                <button className="toggle-btn active" title={t("RequirementsPage.cardView")}><i className="fas fa-th-large"></i></button>
                                <button className="toggle-btn" title={t("RequirementsPage.listView")}><i className="fas fa-list"></i></button>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {loadingSubCategories ? (
                          <div className="loading-subcategories">
                            <div className="loader-animation"></div>
                            <p>{t("RequirementsPage.loadingSubcategories")}</p>
                          </div>
                        ) : subCategories.length > 0 ? (
                          <div className="subcategory-cards-grid">
                            {subCategories
                              .filter(sc => !searchQuery || sc.name.toLowerCase().includes(searchQuery.toLowerCase()))
                              .map((subCategory, idx) => {
                              // Use the same accent colors as the category
                              const accentColors = ['#f59e0b', '#22c55e', '#ef4444', '#a855f7', '#3b82f6'];
                              const accent = accentColors[idx % accentColors.length];
                              
                              return (
                                <div 
                                  key={subCategory.id} 
                                  className={`subcategory-card${expandedSubCategory[subCategory.id] ? ' expanded' : ''}`}
                                >
                                  <div className="subcategory-card-header" style={{ borderTopColor: accent }}>
                                    <div className="subcategory-icon-wrapper" style={{ backgroundColor: accent + '14' }}>
                                      <i className="fas fa-layer-group" style={{ color: accent }}></i>
                                    </div>
                                    <div className="subcategory-title-area">
                                      <h4 className="subcategory-title" onClick={() => handleSubCategoryClick(subCategory)}>
                                        {subCategory.name}
                                      </h4>
                                      <div className="subcategory-meta">
                                        <span className="control-badge">{t("RequirementsPage.controlsCount", { count: subCategory.controls ? subCategory.controls.length : 0 })}</span>
                                      </div>
                                    </div>
                                    <button 
                                      className="expand-card-btn" 
                                      onClick={() => handleToggleSubCategory(subCategory.id)}
                                      aria-label={t("RequirementsPage.toggleControls")}
                                    >
                                      <i className={`fas fa-chevron-${expandedSubCategory[subCategory.id] ? 'up' : 'down'}`}></i>
                                    </button>
                                  </div>
                                  
                                  {expandedSubCategory[subCategory.id] && subCategory.controls && (
                                    <div className="subcategory-card-content">
                                      <div className="controls-list">
                                        {subCategory.controls.length > 0 ? (
                                          subCategory.controls.map((control) => {
                                            const owner = controlOwners.find(owner => owner.controlId === control.id);
                                            const ownerName = owner ? `${owner.firstName}` : t("RequirementsPage.unassigned");
                                            
                                            return (
                                              <div
                                                key={control.id}
                                                className="control-item"
                                                onClick={() => handleControlClick(control)}
                                              >
                                                <div className="control-item-left">
                                                  <div className="control-status" style={{ backgroundColor: getStatusColor(control.status) }}>
                                                    <i className={getStatusIcon(control.status)}></i>
                                                  </div>
                                                </div>
                                                <div className="control-item-middle">
                                                  <div className="control-name">{control.name}</div>
                                                  <div className="control-desc">{limitWords(control.description, 10)}</div>
                                                </div>
                                                <div className="control-item-right">
                                                  <div className="control-owner">
                                                    <span className="owner-label">{t("RequirementsPage.ownerLabel")}</span>
                                                    <span className="owner-name">{ownerName}</span>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })
                                        ) : (
                                          <div className="no-controls-message">
                                            <i className="fas fa-info-circle"></i>
                                            <p>{t("RequirementsPage.noControlsAvailable")}</p>
                                          </div>
                                        )}
                                      </div>
                                      
                                      <div className="controls-actions">
                                        <button className="action-btn secondary">
                                          <i className="fas fa-download"></i> {t("RequirementsPage.export")}
                                        </button>
                                        <button className="action-btn primary" style={{ backgroundColor: accent }}>
                                          <i className="fas fa-eye"></i> {t("RequirementsPage.viewAll")}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-subcategories">
                            <div className="empty-state-icon">
                              <i className="fas fa-folder-open"></i>
                            </div>
                            <h4>{t("RequirementsPage.noSubcategoriesTitle")}</h4>
                            <p>{t("RequirementsPage.noSubcategoriesBody")}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* We don't need the helper functions wrapper anymore since we moved them to component scope */}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          ) : (
            <div className="no-selection">
              <div className="no-selection-content">
                <i className="fas fa-clipboard-list"></i>
                <h3>{t("RequirementsPage.selectCategoryTitle")}</h3>
                <p>{t("RequirementsPage.selectCategoryBody")}</p>
              </div>
            </div>
          )}
        </div>
      </div>

  
{(selectedSubCategory || selectedControl) && (
  <div
    className={`right-tab ${
      selectedSubCategory || selectedControl? 'open' : ''
    }`}
    ref={tabRef}
  >
    <button className="close-btn" onClick={handleCloseTab}>
      ×
    </button>
    <h4>
      {selectedSubCategory
        ? t("RequirementsPage.subcategoryDetails")
        : selectedControl
        ? t("RequirementsPage.controlDetails")
        : t("RequirementsPage.documentDetails")}
    </h4>

    {selectedSubCategory && (
      <>
        <div className="control-field">
          <label>{t("RequirementsPage.nameLabel")}</label>
          <span>{selectedSubCategory.name}</span>
        </div>
        <div className="control-field">
          <label>{t("RequirementsPage.descriptionLabel")}</label>
          <span>{selectedSubCategory.description}</span>
        </div>
      </>
    )}

    {selectedControl && (
      <>
        <div className="control-field">
          <label>{t("RequirementsPage.nameLabel")}</label>
          <span>{selectedControl.name}</span>
        </div>
        <div className="control-field">
          <label>{t("RequirementsPage.descriptionLabel")}</label>
          <span>{selectedControl.description}</span>
        </div>
        <div className="control-field">
          <label>{t("RequirementsPage.keyLabel")}</label>
          <span>{selectedControl.key}</span>
        </div>
        <div className="control-field">
          <label>{t("RequirementsPage.ownerFieldLabel")}</label>
          <select
            value={selectedControl?.owner}
            onChange={handleOwnerChange}
            disabled={userRole === 'ROLE_ADMIN'}
          >
            <option value="">{t("RequirementsPage.selectOwner")}</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.firstName}
              </option>
            ))}
          </select>
        </div>
        <div className="document-container">
          {documentName.length > 0 ? (
            <div className="document-list">
              <p>{t("RequirementsPage.documentsAssigned")}</p>
              {documentName.map((doc) => (
                <span
                  key={doc.id}
                  className="document-name"
                  onClick={() => handleDocumentClick(doc.id)}
                >
                  {doc.title}
                </span>
              ))}
            </div>
          ) : (
            <p>{t("RequirementsPage.noDocumentsAssigned")}</p>
          )}
        </div>
      </>
    )}
  </div>
)}
    </div>
  );
}

export default RequirementsPage;
